import { Link } from 'react-router-dom'
import { ArrowLeft, Info, BotMessageSquare, MessageCircle, Volume2, Languages } from 'lucide-react'
import { TextToSpeechGallery } from '@/components/gallery/TextToSpeechGallery'
import { SpeechGalleryItem } from '@/models/gallery'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/SpeechPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'OpenAI Speech',
    shortName: 'OpenAI TTS',
    modelKey: 'openai/gpt-4o-mini-tts',
    creditsPerRequest: 0.1,
    voiceCount: 11,
    languageCount: 100,
    heroAudioSrc: 'https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-hero.mp3',
    providerUrl: 'https://platform.openai.com/docs/guides/text-to-speech',
    providerName: 'OpenAI TTS',
    pageUrl: 'https://docs.aibrush.co/speech-models/openai-speech',
} as const;

const textToSpeechGalleryItems: SpeechGalleryItem[] = [
    {
        id: "openai-speech-1",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-1.mp3",
        className: "speech-item",
        prompt: `What's up, sports fans?! Welcome to The Final Whistle! I'm your host, and today, we're breaking down last night's epic overtime thriller! The crowd was electric, the players fired up, and the Artica Aces pulled off a comeback for the ages! We'll dive into the key plays and standout moments, and what this means for the rest of the season. This one's packed with heart-pounding action, so grab your snacks, and let's get into it!`,
        tone: `Voice Affect: Energetic and animated; dynamic with variations in pitch and tone. Tone: Excited and enthusiastic. Pacing: Rapid delivery. Emotion: Intensely focused, and excited. Personality: Relatable and engaging.`,
        voice: "alloy",
        voiceDescription: ""
    },
    {
        id: "openai-speech-2",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-2.mp3",
        className: "speech-item",
        prompt: `Ah, noble traveler! Heed my words, and I shall lead thee to the fabled Holeful Bakery! Step forth upon West 74th Street, marching straight with purpose. When thou dost reach the great crossing at Columbus Avenue, turn left, as if answering the call to adventure! Continue southward, past bustling merchants and townfolk, until thou dost arrive at Amsterdam Avenue. Here, turn right, for the scent of warm-baked glory draws near! Lo! Just ahead, the crest of Levain Bakery stands proud. Enter, noble traveler, and claim thy rightful reward—a golden, gooey treasure beyond measure! Go forth, and may thy quest be delicious and true!`,
        tone: `Affect: Deep, commanding, and slightly dramatic. Tone: Noble, heroic, and formal. Emotion: Excitement, anticipation, and a sense of mystery. Pronunciation: Clear, deliberate, and with a slightly formal cadence.`,
        voice: "ash",
        voiceDescription: ""
    },
    {
        id: "openai-speech-3",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-3.mp3",
        className: "speech-item",
        prompt: `Ah, buonissima sera, my friends! Tonight, we have something truly special for you. To start, a classic bruschetta al pomodoro—crispy bread, sweet tomatoes, a drizzle of olive oil, semplice e perfetto! For the main, you must try our ossobuco alla milanese, slow-braised veal shank, so tender it falls off the bone, served with rich risotto allo zafferano—golden, creamy, bellissimo! And for dessert? Ah, a slice of torta della nonna, delicate pastry, creamy custard, a dusting of powdered sugar—just like Nonna used to make. Mangia bene, enjoy, and buon appetito!`,
        tone: `Affect/Personality: An exuberant Italian chef. Tone: Passionate about the quality and the ingredients. Pronunciation: Italian mixed with English. Emotion: Warm, exuberant, and patient.`,
        voice: "ballad",
        voiceDescription: ""
    },
    {
        id: "openai-speech-4",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-4.mp3",
        className: "speech-item",
        prompt: `Ho ho ho! Merry Christmas! You've reached Santa's workshop. How can I help you today? For toy requests, press 1. If you're on the nice list, press 2. If you're on the naughty list, press 3. To speak to an Elf, press 4. Don't worry, we're here to make sure every wish is granted! Ho ho ho, Merry Christmas, and I hope you're having a magical holiday season!`,
        tone: `Identity: Santa Claus. Affect: Jolly, warm, and cheerful. Tone: Festive and welcoming. Emotion: Joyful and playful. Pronunciation: Clear and exaggerated festive phrases.`,
        voice: "coral",
        voiceDescription: ""
    },
    {
        id: "openai-speech-5",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-5.mp3",
        className: "speech-item",
        prompt: `Today, we're going to paint a simple landscape. Get comfortable, grab your brushes, and let's begin. Start with a medium-sized brush, dipping it into pale blue paint. Using soft, horizontal strokes, create a gentle sky across the top third of your canvas. Rinse and dry your brush, then mix soft green with a touch of white. Start halfway down the canvas and paint a smooth, curving line for rolling hills. Let your brush follow the natural curve, creating peaceful hillsides.`,
        tone: `Accent/Affect: Warm, refined, art instructor. Tone: Calm, encouraging, and articulate. Pacing: Slow and deliberate. Emotion: Cheerful, supportive, and enthusiastically appreciative of art.`,
        voice: "echo",
        voiceDescription: ""
    },
    {
        id: "openai-speech-6",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-6.mp3",
        className: "speech-item",
        prompt: `Oh yeah… welcome, cool cat, to the smoothest ride in online car shopping. Sit back, relax, and let me guide you through this fine selection of four-wheeled wonders. First up, if you're looking to browse all vehicles, just slide that cursor over and hit Enter. Feeling fancy? Filter by make, model, or year, and let the magic happen. Mmm, got your eye on something special? Click that "View Details" button, and I'll lay down the specs nice and easy. When you're ready to make a move, just cruise on over to "Apply for Financing"—no bumps in the road here. So go ahead, take your time, and let the ride find you. Stay smooth, stay stylish… and happy shopping. Mmm, yeah.`,
        tone: `Voice: Deep, velvety, like a late-night jazz radio host. Tone: Smooth, laid-back, and inviting. Personality: Confidence, charm, and playful sophistication. Phrasing: Fluid and conversational.`,
        voice: "fable",
        voiceDescription: ""
    },
    {
        id: "openai-speech-7",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-7.mp3",
        className: "speech-item",
        prompt: `Good afternoon, team. Here are the key takeaways from today's budget planning meeting. First, departmental allocations were reviewed, with a focus on aligning resources with projected growth areas. Adjustments were proposed for marketing and operations, increasing their budgets by 5% to support expansion initiatives. Second, cost-saving measures were discussed, including vendor negotiations and process automation, aiming for a 10% reduction in overhead expenses. Lastly, the team set a deadline of next Friday to finalize projections and ensure alignment with company goals. Action items have been assigned, and follow-ups will be scheduled accordingly. Thank you all for your input—let's keep this process efficient and on track.`,
        tone: `Voice: Clear, authoritative, and composed. Tone: Neutral and informative formality. Delivery: Steady and measured with emphasis on critical points.`,
        voice: "nova",
        voiceDescription: ""
    },
    {
        id: "openai-speech-8",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-8.mp3",
        className: "speech-item",
        prompt: `I'm so sorry you're dealing with these connection issues. I know how disruptive this can be, especially during important meetings. Let's start by checking the basics—make sure all cables to your router and modem are secure. If you see any red or amber blinking lights, that could signal a problem. I'll also run a quick diagnostic from our end, which may briefly disconnect your internet. This will help us find the issue and resolve it quickly. I appreciate your patience, and I'm here to help you every step of the way!`,
        tone: `Voice: Warm, empathetic, and professional. Delivery: Calm and patient. Phrasing: Clear and concise customer-friendly language. Tone: Empathetic and solution-focused.`,
        voice: "onyx",
        voiceDescription: ""
    },
    {
        id: "openai-speech-9",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-9.mp3",
        className: "speech-item",
        prompt: `Ah, you got a package gone missing, huh? Sounds like trouble. Lemme see what I can dig up. I'm chasing a trail of numbers through the system. There it is—shipped two days ago, supposed to land on your doorstep by noon today. But… it's still out there. Somewhere. Maybe it's stuck at a warehouse, maybe it took a wrong turn down a dark alley. Either way, I'll get to the bottom of it. I'll send you the latest update, and if it doesn't show up soon… well, I'll make some calls. You sit tight, kid. I'll keep an eye on it.`,
        tone: `Affect: A mysterious noir detective. Tone: Cool, detached, but subtly reassuring. Delivery: Slow and deliberate with dramatic pauses. Emotion: World-weariness and quiet determination.`,
        voice: "sage",
        voiceDescription: ""
    },
    {
        id: "openai-speech-10",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-10.mp3",
        className: "speech-item",
        prompt: `Yeah, yeah, ya got Big Apple Insurance, whaddaya need? Let's make this quick, I got places to be. If ya gotta file a claim, press 1—lemme guess, someone cut ya off? Figures. Wanna check the status? Press 2, I know, I know, hurry it up, right? If ya just wanna hold, press 3—hey, your call, but don't say I didn't warn ya. Need a real person? Press 4, and I'll get ya through—just don't start yellin' at 'em, they're doin' their best. Alright, let's move it along, time is money, buddy!`,
        tone: `Voice: Gruff, fast-talking, New York cabbie. Tone: Slightly exasperated efficiency. Dialect: Strong New York accent. Pronunciation: Quick and clipped.`,
        voice: "shimmer",
        voiceDescription: ""
    },
    {
        id: "openai-speech-11",
        audioSrc: "https://cdn.aibrush.co/docs/audio/models/openai-speech/sample-11.mp3",
        className: "speech-item",
        prompt: `Woohoo! Let's get those meetings scheduled, superstar! Alright, first up—Monday at 10 AM, you've got a check-in with the team. Go, teamwork! Next, we're locking in Wednesday at 2 PM for that client presentation—you got this! Oh, and don't forget Friday at 4 PM, a strategy session to wrap up the week. Finish strong! Need to move anything around? No worries—I'm here to keep your schedule totally winning! Just say the word, and I'll make it happen! Let's do this!`,
        tone: `Personality/affect: A high-energy cheerleader. Voice: Enthusiastic, bubbly, and uplifting. Tone: Encouraging and playful. Dialect: Casual and upbeat.`,
        voice: "verse",
        voiceDescription: ""
    }
];

export default function OpenAISpeechPage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection, styles.speechHeroAnimated)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Experience cutting-edge <strong>Text to Speech</strong> generation with OpenAI's most advanced speech synthesis models.
                        Featuring <strong>{MODEL_CONFIG.voiceCount} distinct voices</strong> and support for <strong>{MODEL_CONFIG.languageCount}+ languages</strong>.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <MessageCircle size={20} {...stylex.props(styles.statSvg)} />
                            <span>Text to Speech</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Volume2 size={20} {...stylex.props(styles.statSvg)} />
                            <span>{MODEL_CONFIG.voiceCount} Voice Options</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Languages size={20} {...stylex.props(styles.statSvg)} />
                            <span>{MODEL_CONFIG.languageCount}+ Languages</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerRequest} credits per request</div>

                    {/* Featured Voice Player */}
                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src={MODEL_CONFIG.heroAudioSrc} type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                </div>

                {/* Floating Icons Container */}
                <div {...stylex.props(styles.floatingIconsContainer)}>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon1)}>
                        <MessageCircle size={120} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon2)}>
                        <Languages size={80} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon3)}>
                        <Volume2 size={50} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                </div>
            </section>

            {/* Important Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <Info size={24} />
                </div>
                <div {...stylex.props(styles.noteContent)}>
                    <h4 {...stylex.props(styles.noteContentH4)}>Important Note</h4>
                    <p {...stylex.props(styles.noteContentP)}>
                        <strong>These voice examples are demonstrations of what's possible</strong> - they are not restrictive!
                        Every voice in the gallery can adapt to <strong>any language, tone, emotion, or speaking style</strong> you specify in your prompt.
                        The same voice can be cheerful, serious, excited, calm, or any other tone you need.
                        Think of these as starting points - your creativity and prompt engineering will unlock the full potential of each voice.
                    </p>
                </div>
            </div>

            {/* AI Assistance Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <div {...stylex.props(styles.inlineChatButton)}>
                        <BotMessageSquare size={22} />
                    </div>
                </div>
                <div {...stylex.props(styles.noteContent)}>
                    <p {...stylex.props(styles.noteContentP)}>
                        <strong>Need help with voice selection or prompt writing?</strong> Click the
                        chat icon in the top-right corner of any component to get AI assistance with your speech creation!
                    </p>
                </div>
            </div>

            {/* Speech Gallery */}
            <TextToSpeechGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerRequest} credits</strong> per request.`}
                title="Text to Speech Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.providerName}</a> through these AI-generated speech samples. With advanced neural networks and natural language processing, ${MODEL_CONFIG.name} delivers high-quality, human-like voice synthesis across multiple languages and accents. <strong>Click on any audio sample</strong> to hear how it was created.`}
                items={textToSpeechGalleryItems} />
        </div>
    )
}